// @ts-nocheck
// app/learn/index.tsx — KURSI LEARN landing page (editorial light theme)

import { View, Text, ScrollView, StyleSheet, Platform, Pressable } from 'react-native';
import { router } from 'expo-router';
import { ArrowLeft } from 'lucide-react-native';
import {
  ACCENT, ACCENT_SOFT, ACCENT_DEEP,
  INK, INK_SOFT, INK_MUTED, INK_FAINT,
  PAPER, PAPER_SOFT, PAPER_DEEP,
  FOREST, theme,
} from '@/constants/theme';
import {
  EditorialButton, Section, useEditorialBreakpoint,
} from '@/components/editorial';
import { EditorialHeader } from '@/components/editorial/EditorialHeader';

const SANS        = theme.fonts.regular;
const SANS_MED    = theme.fonts.medium;
const SANS_BOLD   = theme.fonts.bold;
const SANS_SEMI   = theme.fonts.semiBold;
const SANS_BLACK  = theme.fonts.serifBlack;
const IS_WEB      = Platform.OS === 'web';

const OUTCOMES = [
  {
    title: 'שיעורים מובנים מתוך החומר שלכם',
    body: 'המערכת מפרקת את הקבצים לנושאים, בונה סדר לימוד, ומציגה שיעורים קצרים עם נקודות מפתח — לא טקסט גולמי.',
  },
  {
    title: 'תרגול ממוקד לפי נושא ורמת שליטה',
    body: 'מרכז התרגול מושך שאלות בדיוק מהמקומות החלשים, כדי שכל סשן יתמקד במה שחוסם התקדמות.',
  },
  {
    title: 'סקירת טעויות עם מסלול תיקון',
    body: 'כל טעות נכנסת למסלול תיקון: הסבר קצר, ניסיון חוזר, ומדידה אם הנושא נסגר בפועל.',
  },
  {
    title: 'מדד מוכנות לבחינה ותמיכת מנחה אישי',
    body: 'מצב בחינה מדמה לחץ אמיתי, והמנחה האישי נותן חיזוק נקודתי כשנתקעים — בלי לחרוג מהחומר שלכם.',
  },
];

const PROCESS = [
  {
    step: '01',
    title: 'מעלים חומר ומגדירים יעד מבחן',
    body: 'PDF, סיכומים או מצגות נכנסים למנוע. בוחרים תאריך יעד, רמה, וקצב שבועי כדי שהמסלול יתאים לזמן האמיתי.',
    tag: 'קליטה ופירוק תוכן',
  },
  {
    step: '02',
    title: 'המערכת בונה מסלול לימוד פעיל',
    body: 'התוכן הופך לפרקים, שיעורים ומשימות. כל יחידה מסתיימת בהחלטה ברורה: ממשיכים, מתרגלים, או חוזרים לנקודה חלשה.',
    tag: 'בניית מסלול דינמית',
  },
  {
    step: '03',
    title: 'תרגול, תיקון טעויות ומוכנות מדידה',
    body: 'תרגול + סקירה + מבחן עובדים כרצף אחד. בסוף כל סבב רואים מוכנות, פערים, ומה עושים בפעם הבאה.',
    tag: 'סגירת פערים לפני בחינה',
  },
];

const CREDIBILITY_POINTS = [
  'אחוז הצלחה לפי נושא, לא רק ציון כללי.',
  'זמן תגובה ממוצע לתשובה — כדי לזהות שליטה אמיתית.',
  'רשימת טעויות חוזרות לפני ואחרי תיקון.',
  'סימולציית מבחן עם מגבלת זמן ודוח סיכום ברור.',
];

export default function KursiLearnLanding() {
  const { isMobile, isDesktop } = useEditorialBreakpoint();

  return (
    <View style={{ flex: 1, backgroundColor: PAPER }}>
      <EditorialHeader />
      <ScrollView
        style={{ flex: 1, backgroundColor: PAPER }}
        contentContainerStyle={{ backgroundColor: PAPER }}
        showsVerticalScrollIndicator={false}
      >
        {/* ─── HERO ─────────────────────────────────────────────────────── */}
        <View style={s.heroSection}>
          {IS_WEB && (
            <>
              <View style={s.heroOrb1} pointerEvents="none" />
              <View style={s.heroOrb2} pointerEvents="none" />
            </>
          )}
          <Section py="xl" px="md" max={1100}>
            <View style={{ alignItems: 'flex-end' }}>
              <Text style={s.heroKicker}>KURSI LEARN</Text>
              <View style={{ height: 16 }} />
              <Text style={s.heroTitle}>
                הופכים חומר גולמי{'\n'}
                <Text style={s.heroTitleAccent}>למסלול לימוד</Text> חד ומוכן למבחן
              </Text>
              <View style={{ height: 20 }} />
              <Text style={s.heroSub}>
                מעלים חומר פעם אחת, מקבלים רצף ברור: לימוד ← תרגול ← תיקון טעויות ← מוכנות לבחינה.
              </Text>
              <View style={{ height: 36 }} />
              <View style={[s.heroCtaRow, { flexDirection: isDesktop ? 'row-reverse' : 'column' } as any]}>
                <EditorialButton
                  label="התחילו לבנות מסלול"
                  onPress={() => router.push('/learn/path/create' as any)}
                  variant="primary"
                  size="lg"
                  trailing={<ArrowLeft size={15} color={PAPER_SOFT} />}
                />
                <Text style={s.heroNote}>חינם להתחלה · התחברות נדרשת רק לפני יצירה</Text>
              </View>
            </View>
          </Section>
        </View>

        {/* ─── OUTCOMES ─────────────────────────────────────────────────── */}
        <View style={s.outcomesSection}>
          <Section py="xxl" px="md" max={1100}>
            <Text style={s.sectionKicker}>מה מקבלים בפועל</Text>
            <View style={{ height: 12 }} />
            <Text style={s.sectionTitle}>ארבעה יתרונות. כולם מדידים.</Text>
            <View style={{ height: 40 }} />

            <View style={[s.outcomesGrid, { flexDirection: isDesktop ? 'row-reverse' : 'column' } as any]}>
              {/* Featured card */}
              <View style={[s.outcomeFeatured, { flex: isDesktop ? 1.2 : undefined }]}>
                <Text style={s.featuredKicker}>תמונת מצב מיידית</Text>
                <View style={{ height: 12 }} />
                <Text style={s.featuredTitle}>
                  במקום עוד קורס כללי — מסלול שעובד על החומר שלכם בלבד
                </Text>
                <View style={{ height: 10 }} />
                <Text style={s.featuredBody}>
                  כל יחידת לימוד, תרגול ותיקון טעויות נבנית על התוכן שהעלתם. אין מעבר לנושאים לא רלוונטיים ואין ניחושים.
                </Text>
              </View>

              {/* List */}
              <View style={[s.outcomesList, { flex: 1 }]}>
                {OUTCOMES.map((item, idx) => (
                  <View key={item.title} style={s.outcomeRow}>
                    <View style={s.outcomeIndexWrap}>
                      <Text style={s.outcomeIndex}>{String(idx + 1).padStart(2, '0')}</Text>
                    </View>
                    <View style={{ flex: 1, gap: 4 }}>
                      <Text style={s.outcomeTitle}>{item.title}</Text>
                      <Text style={s.outcomeBody}>{item.body}</Text>
                    </View>
                  </View>
                ))}
              </View>
            </View>
          </Section>
        </View>

        {/* ─── PROCESS ─────────────────────────────────────────────────── */}
        <View style={s.processSection}>
          <Section py="xxl" px="md" max={1100}>
            <Text style={s.sectionKicker}>מהעלאה עד מוכנות</Text>
            <View style={{ height: 12 }} />
            <Text style={s.sectionTitle}>שלושה שלבים. בלי הפתעות.</Text>
            <View style={{ height: 40 }} />

            <View style={{ flexDirection: isDesktop ? 'row-reverse' : 'column', gap: 16 } as any}>
              {PROCESS.map((item, idx) => (
                <View key={item.step} style={[s.processCard, { flex: 1 }]}>
                  <Text style={s.processTag}>{item.tag}</Text>
                  <View style={{ height: 16 }} />
                  <Text style={s.processStep}>{item.step}</Text>
                  <View style={{ height: 8 }} />
                  <Text style={s.processTitle}>{item.title}</Text>
                  <View style={{ height: 8 }} />
                  <Text style={s.processBody}>{item.body}</Text>
                </View>
              ))}
            </View>
          </Section>
        </View>

        {/* ─── CREDIBILITY ─────────────────────────────────────────────── */}
        <View style={s.credSection}>
          <Section py="xxl" px="md" max={1100}>
            <View style={{ flexDirection: isDesktop ? 'row-reverse' : 'column', gap: 48 } as any}>
              <View style={{ flex: 1, alignItems: 'flex-end' }}>
                <Text style={s.sectionKicker}>אמון לפני הבטחות</Text>
                <View style={{ height: 12 }} />
                <Text style={[s.sectionTitle, { color: PAPER_SOFT }]}>
                  איך מודדים תוצאות בצורה אמינה
                </Text>
                <View style={{ height: 16 }} />
                <Text style={s.credLead}>
                  במקום עדויות כלליות, KURSI LEARN מציג מדדים שאפשר לבדוק בכל רגע — מה השתפר, מה עדיין חלש, ומה צריך לתרגל עכשיו.
                </Text>
              </View>

              <View style={{ flex: 1, gap: 10 }}>
                {CREDIBILITY_POINTS.map((point) => (
                  <View key={point} style={s.credItem}>
                    <Text style={s.credItemText}>{point}</Text>
                  </View>
                ))}
              </View>
            </View>
          </Section>
        </View>

        {/* ─── CTA BOTTOM ──────────────────────────────────────────────── */}
        <Section py="xl" px="md" max={1100}>
          <View style={s.ctaBottom}>
            <Text style={s.ctaBottomTitle}>מוכנים להתחיל?</Text>
            <View style={{ height: 8 }} />
            <Text style={s.ctaBottomSub}>מעלים חומר, מגדירים יעד, מקבלים מסלול. מוכנות מדידה מתחילה ביום הראשון.</Text>
            <View style={{ height: 28 }} />
            <EditorialButton
              label="התחילו לבנות מסלול"
              onPress={() => router.push('/learn/path/create' as any)}
              variant="accent"
              size="lg"
              trailing={<ArrowLeft size={15} color={PAPER_SOFT} />}
            />
          </View>
        </Section>
      </ScrollView>
    </View>
  );
}

const s = StyleSheet.create({
  // Hero
  heroSection: {
    backgroundColor: PAPER,
    position: 'relative',
    overflow: IS_WEB ? 'hidden' : 'visible',
    borderBottomWidth: 1,
    borderBottomColor: `${ACCENT}12`,
  } as any,
  heroOrb1: {
    position: 'absolute',
    top: -100,
    right: -80,
    width: 500,
    height: 500,
    borderRadius: 250,
    ...(IS_WEB ? {
      background: `radial-gradient(ellipse at center, ${ACCENT}14 0%, transparent 70%)`,
      filter: 'blur(60px)',
    } as any : { backgroundColor: `${ACCENT}08` }),
  } as any,
  heroOrb2: {
    position: 'absolute',
    bottom: -60,
    left: -60,
    width: 360,
    height: 360,
    borderRadius: 180,
    ...(IS_WEB ? {
      background: `radial-gradient(ellipse at center, ${ACCENT_DEEP}10 0%, transparent 70%)`,
      filter: 'blur(80px)',
    } as any : { backgroundColor: `${ACCENT_DEEP}06` }),
  } as any,
  heroKicker: {
    fontFamily: SANS_SEMI,
    fontSize: 11,
    color: ACCENT,
    letterSpacing: 2,
    textTransform: 'uppercase' as any,
  },
  heroTitle: {
    fontFamily: SANS_BLACK,
    fontSize: IS_WEB ? 56 : 36,
    lineHeight: IS_WEB ? 64 : 44,
    color: INK,
    letterSpacing: IS_WEB ? -1.5 : -0.8,
    textAlign: 'right',
  },
  heroTitleAccent: {
    color: ACCENT,
  },
  heroSub: {
    fontFamily: SANS,
    fontSize: IS_WEB ? 18 : 16,
    lineHeight: IS_WEB ? 30 : 26,
    color: INK_SOFT,
    textAlign: 'right',
    maxWidth: 560,
  },
  heroCtaRow: {
    alignItems: 'flex-end',
    gap: 16,
    flexWrap: 'wrap' as any,
  } as any,
  heroNote: {
    fontFamily: SANS,
    fontSize: 13,
    color: INK_MUTED,
    textAlign: 'right',
  },

  // Outcomes
  outcomesSection: {
    backgroundColor: PAPER_SOFT,
    borderBottomWidth: 1,
    borderBottomColor: `${ACCENT}10`,
  },
  sectionKicker: {
    fontFamily: SANS_SEMI,
    fontSize: 11,
    color: ACCENT,
    letterSpacing: 1.6,
    textTransform: 'uppercase' as any,
    textAlign: 'right',
  },
  sectionTitle: {
    fontFamily: SANS_BLACK,
    fontSize: IS_WEB ? 36 : 26,
    color: INK,
    letterSpacing: IS_WEB ? -0.8 : -0.4,
    lineHeight: IS_WEB ? 44 : 34,
    textAlign: 'right',
  },
  outcomesGrid: {
    gap: 20,
    alignItems: 'flex-start',
  },
  outcomeFeatured: {
    backgroundColor: INK,
    borderRadius: 16,
    padding: 28,
    borderWidth: 1,
    borderColor: `${ACCENT}30`,
    ...(IS_WEB ? { boxShadow: `0 4px 32px ${ACCENT}12` } as any : {}),
  },
  featuredKicker: {
    fontFamily: SANS_SEMI,
    fontSize: 11,
    color: ACCENT,
    letterSpacing: 1.4,
    textTransform: 'uppercase' as any,
    textAlign: 'right',
  },
  featuredTitle: {
    fontFamily: SANS_BLACK,
    fontSize: IS_WEB ? 22 : 20,
    color: PAPER_SOFT,
    lineHeight: IS_WEB ? 32 : 30,
    textAlign: 'right',
    letterSpacing: -0.4,
  },
  featuredBody: {
    fontFamily: SANS,
    fontSize: 14,
    color: INK_FAINT,
    lineHeight: 22,
    textAlign: 'right',
  },
  outcomesList: {
    gap: 10,
  },
  outcomeRow: {
    flexDirection: 'row-reverse',
    gap: 14,
    backgroundColor: PAPER,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: `${ACCENT}12`,
    padding: 16,
    alignItems: 'flex-start',
  } as any,
  outcomeIndexWrap: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: ACCENT_SOFT,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
    flexShrink: 0,
  },
  outcomeIndex: {
    fontFamily: SANS_BOLD,
    fontSize: 12,
    color: ACCENT_DEEP,
  },
  outcomeTitle: {
    fontFamily: SANS_BOLD,
    fontSize: 16,
    color: INK,
    textAlign: 'right',
    letterSpacing: -0.2,
  },
  outcomeBody: {
    fontFamily: SANS,
    fontSize: 14,
    color: INK_SOFT,
    lineHeight: 22,
    textAlign: 'right',
  },

  // Process
  processSection: {
    backgroundColor: PAPER,
    borderBottomWidth: 1,
    borderBottomColor: `${ACCENT}10`,
  },
  processCard: {
    backgroundColor: PAPER_SOFT,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: `${ACCENT}15`,
    padding: 24,
    ...(IS_WEB ? { transition: 'box-shadow 200ms ease' } as any : {}),
  },
  processTag: {
    fontFamily: SANS_SEMI,
    fontSize: 11,
    color: ACCENT,
    letterSpacing: 1.2,
    textTransform: 'uppercase' as any,
    textAlign: 'right',
  },
  processStep: {
    fontFamily: SANS_BLACK,
    fontSize: 13,
    color: `${ACCENT}60`,
    letterSpacing: 0.8,
    textAlign: 'right',
  },
  processTitle: {
    fontFamily: SANS_BOLD,
    fontSize: IS_WEB ? 18 : 17,
    color: INK,
    lineHeight: IS_WEB ? 27 : 25,
    textAlign: 'right',
    letterSpacing: -0.3,
  },
  processBody: {
    fontFamily: SANS,
    fontSize: 14,
    color: INK_MUTED,
    lineHeight: 22,
    textAlign: 'right',
  },

  // Credibility
  credSection: {
    backgroundColor: INK,
  },
  credLead: {
    fontFamily: SANS,
    fontSize: IS_WEB ? 16 : 15,
    color: INK_FAINT,
    lineHeight: IS_WEB ? 26 : 24,
    textAlign: 'right',
    maxWidth: 460,
  },
  credItem: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.03)',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  credItemText: {
    fontFamily: SANS,
    fontSize: 14,
    color: PAPER_SOFT,
    lineHeight: 22,
    textAlign: 'right',
  },

  // Bottom CTA
  ctaBottom: {
    backgroundColor: PAPER_SOFT,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: `${ACCENT}15`,
    padding: IS_WEB ? 48 : 28,
    alignItems: 'flex-end',
    marginVertical: 16,
    ...(IS_WEB ? { boxShadow: `0 2px 0 ${ACCENT}08` } as any : {}),
  },
  ctaBottomTitle: {
    fontFamily: SANS_BLACK,
    fontSize: IS_WEB ? 32 : 24,
    color: INK,
    letterSpacing: -0.6,
    textAlign: 'right',
  },
  ctaBottomSub: {
    fontFamily: SANS,
    fontSize: 16,
    color: INK_SOFT,
    lineHeight: 26,
    textAlign: 'right',
    maxWidth: 520,
  },
});
